import { ShepherdOptionsWithType } from 'react-shepherd'
import { removeEmptySteps, waitForElementExists } from '..'
import tourCompletionImg from './tour-completion-img.png'
import { conditionalStage } from '../../utils'

export default function (
  isMobile: boolean,
  clearAndCloseDraft: () => void
): ShepherdOptionsWithType[] {
  const steps = [
    {
      text: `<img src="${tourCompletionImg}" /><span class="centered-text">Welcome to Social Curator!<br/>Can we show you how to create a post?</span>`,
      buttons: [
        {
          text: 'No, Thank you.',
          type: 'cancel'
        },
        {
          text: 'Yes',
          type: 'next'
        }
      ]
    },
    conditionalStage(isMobile, {
      text: 'Open the <b>Side Bar</b>.',
      attachTo: {
        element: '#app-header #mobile-menu-btn',
        on: 'bottom-end'
      },
      advanceOn: {
        event: 'click',
        selector: '#app-header #mobile-menu-btn'
      },
      beforeShowPromise: async () => {
        return waitForElementExists('#app-header #mobile-menu-btn')
      }
    }),
    {
      text: 'Go to <b>Captions</b>.',
      attachTo: {
        element: '#captions-tab',
        on: isMobile ? 'bottom-start' : 'right-start'
      },
      advanceOn: {
        event: 'click',
        selector: '#captions-tab'
      },
      beforeShowPromise: async () => {
        return waitForElementExists('#captions-tab')
      },
      when: { show: () => clearAndCloseDraft() }
    },
    {
      text: 'Click the <b>USE</b> button to add this caption template to the <b>Draft Editor</b>.',
      attachTo: {
        element: '[data-tour-id="caption"]:first-child [data-tour-id="caption-use-btn"]',
        on: 'top'
      },
      advanceOn: {
        event: 'click',
        selector: '[data-tour-id="caption"]:first-child [data-tour-id="caption-use-btn"]'
      },
      scrollTo: {
        behavior: 'smooth',
        block: 'center'
      },
      beforeShowPromise: async () => {
        return waitForElementExists(
          '[data-tour-id="caption"]:first-child [data-tour-id="caption-use-btn"]'
        )
      }
    },
    {
      text:
        'This is your <b>Draft Editor</b>. Here you can draft out posts for your social accounts.',
      attachTo: conditionalStage(!isMobile, {
        element: '.MuiDrawer-root > .MuiDrawer-paperAnchorRight',
        on: 'auto'
      }),
      canClickTarget: false,
      buttons: [
        {
          text: 'Next',
          type: 'next'
        }
      ],
      beforeShowPromise: async () => {
        return await new Promise((resolve) => {
          setTimeout(resolve, 1000)
        })
      }
    },
    {
      text: 'Click <b>Close</b> to close the <b>Draft Editor</b>.',
      attachTo: {
        element: '.draft-header-top > #draft-drawer-close-btn',
        on: 'bottom-end'
      },
      advanceOn: {
        event: 'click',
        selector: '.draft-header-top > #draft-drawer-close-btn'
      },
      beforeShowPromise: async () => {
        return waitForElementExists('.draft-header-top > #draft-drawer-close-btn')
      }
    },
    conditionalStage(isMobile, {
      text: 'Open the <b>Side Bar</b>.',
      attachTo: {
        element: '#app-header #mobile-menu-btn',
        on: 'bottom-end'
      },
      advanceOn: {
        event: 'click',
        selector: '#app-header #mobile-menu-btn'
      },
      beforeShowPromise: async () => {
        return waitForElementExists('#app-header #mobile-menu-btn')
      }
    }),
    {
      text: 'Go to the <b>Gallery</b>.',
      attachTo: {
        element: '#gallery-tab',
        on: isMobile ? 'bottom-start' : 'right-start'
      },
      advanceOn: {
        event: 'click',
        selector: '#gallery-tab'
      },
      beforeShowPromise: async () => {
        return waitForElementExists('#gallery-tab')
      }
    },
    {
      text: 'Click the <b>USE</b> button to add this image to your <b>Draft Editor</b>.',
      attachTo: {
        element: '[data-tour-id="photo"]:nth-child(1) [data-tour-id="photo-use-btn"]',
        on: 'top'
      },
      advanceOn: {
        event: 'click',
        selector: '[data-tour-id="photo"]:nth-child(1) [data-tour-id="photo-use-btn"]'
      },
      scrollTo: {
        behavior: 'smooth',
        block: 'center'
      },
      beforeShowPromise: async () => {
        return waitForElementExists(
          '[data-tour-id="photo"]:nth-child(1) [data-tour-id="photo-use-btn"]'
        )
      }
    },
    {
      text: 'Give your draft a title.',
      attachTo: {
        element: '#draft-drawer-header-title-input',
        on: 'auto'
      },
      popperOptions: { modifiers: [{ name: 'focusAfterRender', enabled: false }] },
      buttons: [
        {
          text: 'Next',
          type: 'next'
        }
      ],
      beforeShowPromise: async () => {
        return await new Promise(async (resolve) => {
          await waitForElementExists('#draft-drawer-header-title-input')
          setTimeout(resolve, 1500)
        })
      }
    },
    {
      text: isMobile
        ? 'You can further customize your caption here'
        : 'Edit your caption, until you think it\'s ready to post.',
      attachTo: {
        element: '#draft-drawer-caption-area textarea',
        on: 'auto'
      },
      buttons: [
        {
          text: 'Previous',
          type: 'back'
        },
        {
          text: isMobile ? 'Got it' : 'I\'m Done',
          type: 'next'
        }
      ]
    },
    {
      text: 'Save your post.',
      attachTo: {
        element: '.draft-drawer-actions > #draft-drawer-save-btn',
        on: 'auto'
      },
      advanceOn: {
        event: 'click',
        selector: '.draft-drawer-actions > #draft-drawer-save-btn'
      }
    },
    conditionalStage(!isMobile, {
      text: 'You can find your saved drafts in <b>My Posts</b>.',
      attachTo: {
        element: '#drafts-tab',
        on: 'right-start'
      },
      canClickTarget: false,
      buttons: [
        {
          text: 'Got It',
          type: 'next'
        }
      ]
    }),
    conditionalStage(!isMobile, {
      text: 'You can click the Draft Button to start a draft.',
      attachTo: {
        element: '#drawer-fab',
        on: 'right-end'
      },
      canClickTarget: false,
      buttons: [
        {
          text: 'Previous',
          type: 'back'
        },
        {
          text: 'Got It',
          type: 'next'
        }
      ]
    }),
    {
      text: `<img src="${tourCompletionImg}" /><span class="centered-text">Congrats! You\'re ready to start drafting your own posts using Social Curator. Have a question? Reach out to us by clicking Chat Support.</span>`,
      buttons: [
        {
          text: 'Let\'s Do This',
          type: 'next'
        }
      ]
    }
  ]
  return removeEmptySteps(steps)
}
